import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { ModalContainer } from "../../components/PageComponents";
import {
  CloseIcon,
  CountriesIcon,
  ValidIcon,
  GenerateIcon,
  PercentIcon,
} from "../../icons/promotions";
import { ThreeDotsIcon, CopyIcon, ShareIcon } from "../../icons/promotions";
import { UsageIcon, DiscountIcon, UserGroupIcon } from "../../icons/promotions";
import {
  Input,
  Toggle,
  Select,
  RadioGroup,
  OutlineButton,
  GradientButton,
} from "../../components/forms";
import { PromotionProps, InfoProp } from "./types";

const handleCopyClick = (text: string) => {
  navigator.clipboard.writeText(text);
  toast.success("Copied!");
};

export const Promotion = ({ promotions }: { promotions: PromotionProps[] }) => {
  return (
    <div className="row-8">
      {promotions.map((promotion, index) => {
        return (
          <div
            key={index}
            className="col-4 w-full max-w-80 rounded-lg border border-gray-300 p-4 dark:bg-zinc-900 dark:border-zinc-700"
          >
            {/* First Line */}
            <div className="row-2 justify-between">
              <span className="inter-sm-medium-black dark:text-white">
                {promotion.name}
              </span>
              <ThreeDotsIcon />
            </div>

            {/* Promocode */}
            <button
              onClick={() => handleCopyClick(promotion.promoCode)}
              className="row-2 justify-between border border-gray-400 rounded-lg p-3 dark:border-zinc-600 dark:bg-zinc-800"
            >
              <span className="satoshi-base-bold-gray-900 dark:text-white">
                {promotion.promoCode}
              </span>
              <CopyIcon />
            </button>

            {/* Information */}
            <div className="col-4">
              <Information
                icon={<CountriesIcon />}
                text="Countries"
                content={promotion.countries}
              />
              <div className="relative">
                <Information
                  icon={<ValidIcon />}
                  text="Valid Till"
                  content={promotion.valid}
                />
                <span
                  className={`absolute right-0 top-1/2 transform -translate-y-1/2 border rounded-2xl py-0.5 px-2
                    ${
                      promotion.status === "Ongoing"
                        ? "bg-sux-50 border-sux-200 dark:bg-green-900/20 dark:border-green-800 satoshi-xs-medium-sux-700 dark:text-green-400"
                        : "bg-err-50 border-err-200 dark:bg-red-900/20 dark:border-red-800 satoshi-xs-medium-err-700 dark:text-red-400"
                    }   `}
                >
                  {promotion.status}
                </span>
              </div>
              <Information
                icon={<DiscountIcon />}
                text="Discount Percentage"
                content={promotion.discount + " Discount"}
              />
              <Information
                icon={<UsageIcon />}
                text="Usage Count"
                content={promotion.usage}
              />
              <Information
                icon={<UserGroupIcon />}
                text="User Group"
                content={promotion.group}
              />
            </div>

            {/* Sharable Link */}
            <button
              onClick={() => handleCopyClick(promotion.link)}
              className="rounded-lg border border-gray-300 py-3 px-5 row-3 items-center justify-center dark:border-zinc-700 dark:hover:bg-zinc-800 transition-colors"
            >
              <span className="satoshi-base-bold-gray-900 text-nowrap dark:text-white">
                Copy sharable link
              </span>
              <ShareIcon />
            </button>
          </div>
        );
      })}
    </div>
  );
};

const Information = ({ icon, text, content }: InfoProp) => {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="row-2">
        {icon}
        <span className="satoshi-xs-normal-gray-500 dark:text-zinc-400">
          {text}
        </span>
      </div>
      <span className="satoshi-sm-medium-gray-900 dark:text-zinc-200">
        {content}
      </span>
    </div>
  );
};

export const PromotionModal = ({ close }: { close: () => void }) => {
  const Divider = () => (
    <div className="w-full border-[0.5px] border-gray-300"></div>
  );
  return (
    <ModalContainer
      title="Create new promotion"
      closeIcon={
        <button onClick={close}>
          <CloseIcon />
        </button>
      }
    >
      <div className="col-6">
        <HeadingContainer title="Promotion Details">
          <div className="col-4">
            <Input placeholder="Promotion name" type="text" />
            <div className="row-4 items-center w-full">
              <Input placeholder="Valid till (DD/MM/YY)" type="date" />
              <div className="h-10 border border-gray-300"></div>
              <Toggle text="No End" />
            </div>
          </div>
        </HeadingContainer>
        <Divider />
        <HeadingContainer title="Countries Availability">
          <Select options={countries} type="select" />
        </HeadingContainer>
        <Divider />
        <HeadingContainer title="Discount Details">
          <div className="row-4">
            <RadioGroup
              name="discount"
              dir="row"
              options={[
                { label: "Percentage", value: "one" },
                { label: "Fixed amount", value: "two" },
              ]}
              onChange={(val) => console.log("Selected:", val)}
            />
            <Input placeholder="Amount" icon={{ icon: <PercentIcon /> }} />
          </div>
          <Select options={noLimit} type="select" />
        </HeadingContainer>
        <Divider />
        <HeadingContainer title="Minimum Purchase Requirements">
          <RadioGroup
            name="purchase requirements"
            dir="col"
            options={[
              { label: "No minimum requirement", value: "one" },
              { label: "Minimum purchase amount", value: "two" },
              { label: "Minimum quality of items", value: "three" },
            ]}
            onChange={(val) => console.log("Selected:", val)}
          />
        </HeadingContainer>
        <Divider />
        <HeadingContainer title="User Group">
          <RadioGroup
            name="user group"
            dir="col"
            options={[
              { label: "All users", value: "one" },
              { label: "Specific user groups", value: "two" },
            ]}
            onChange={(val) => console.log("Selected:", val)}
          />
        </HeadingContainer>
        <Divider />
        <HeadingContainer title="Promotion Code">
          <div className="row-4">
            <div>
              <Input
                placeholder="Discount code"
                icon={{ onClick: handleCopyClick, icon: <CopyIcon /> }}
              />
              <span className="satoshi-sm-normal-gray-500 dark:text-white/90">
                Generate code or type in code
              </span>
            </div>
            <button
              onClick={() => {}}
              className="row-3 h-fit items-center border border-gray-300 rounded-lg px-5 py-3 dark:text-white"
            >
              <GenerateIcon />
              <span className="satoshi-base-bold-gray-900 dark:text-white">
                Generate
              </span>
            </button>
          </div>
        </HeadingContainer>
        <div className="row-2 justify-end">
          <OutlineButton content="Cancel" />
          <GradientButton content="Create Promotion" />
        </div>
      </div>
    </ModalContainer>
  );
};

const HeadingContainer = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  return (
    <div className="col-4">
      <span className="satoshi-base-bold-pry-900 dark:text-white">{title}</span>
      {children}
    </div>
  );
};

const countries = ["Nigeria", "Ghana", "Egypt"];

const noLimit = ["Yes", "No"];
